import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, ArrowRight, TrendingUp } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const BlogTeaser = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLatestPosts();
  }, []);

  const fetchLatestPosts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blog?limit=3`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading || posts.length === 0) {
    return null;
  }

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <TrendingUp className="h-6 w-6 text-[#D4A853]" />
            <span className="text-[#D4A853] font-semibold uppercase tracking-wide text-sm">
              Insights & Expertenwissen
            </span>
          </div>
          <h2 className="text-4xl font-bold mb-4" style={{ color: 'var(--navy-900)' }}>
            Aktuelles aus unserem Blog
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: 'var(--gray-700)' }}>
            Praxisnahes Wissen zu Handelsimmobilien, Verkaufsstrategien und Marktentwicklungen
          </p>
        </div>

        {/* Blog Posts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
          {posts.map((post) => (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              className="group"
              data-testid={`blog-teaser-${post.slug}`}
            >
              <Card className="h-full overflow-hidden hover:shadow-2xl transition-all duration-300 border-0">
                {/* Image */}
                {post.hero_image_url && (
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={post.hero_image_url}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/70 to-transparent"></div>
                  </div>
                )}

                {/* Content */}
                <div className="p-6">
                  {/* Date */}
                  <div className="flex items-center gap-2 mb-3 text-sm" style={{ color: 'var(--gray-500)' }}>
                    <Calendar className="h-4 w-4" />
                    <span>{formatDate(post.published_at)}</span>
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {post.tags.slice(0, 2).map(tag => (
                      <Badge
                        key={tag}
                        className="bg-[#EFF6FF] text-[#2563A0] text-xs px-2 py-1"
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold mb-3 line-clamp-2 group-hover:text-[#D4A853] transition-colors" style={{ color: 'var(--navy-800)' }}>
                    {post.title}
                  </h3>

                  {/* Excerpt */}
                  <p className="text-base mb-4 line-clamp-3" style={{ color: 'var(--gray-700)' }}>
                    {post.excerpt}
                  </p>

                  {/* Read More */}
                  <div className="flex items-center gap-2 text-[#2563A0] font-semibold group-hover:gap-4 transition-all">
                    <span>Weiterlesen</span>
                    <ArrowRight className="h-4 w-4" />
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>

        {/* CTA Button */}
        <div className="text-center">
          <Link to="/blog">
            <Button
              size="lg"
              className="bg-[#2563A0] text-white hover:bg-[#1B4D7A] px-8 py-6 text-lg"
              data-testid="view-all-blog-posts"
            >
              Alle Artikel ansehen
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogTeaser;
